import { SearchInput, useDebounce, useFetch } from "@4uhub/lib4uhub";
import { Box, Grid, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReportCard } from "./ReportCard";
import { IReport } from "../../../models/report";
import {
  IReportsSearchParams,
  REPORTS_TRANSLATION_PATH,
  reportsViewService,
} from "./shared";
import NoData from "../../../components/Logs/NoData/NoData";
import { ViewMore } from "../../../components/Dashboards/ViewMore/ViewMore";

export interface IReportGridViewProps {
  typeId: string;
  title: string;
  onReportClick: (report: IReport) => void;
  onReportDelete?: (report: IReport) => void;
  canEditDescription?: boolean;
  onDescriptionSaved?: () => void;
}

const ReportGridViewComponent: React.FC<IReportGridViewProps> = ({
  typeId,
  title,
  onReportClick,
  onReportDelete,
  canEditDescription = false,
  onDescriptionSaved,
}) => {
  const { t } = useTranslation();

  const [reports, setReports] = useState<IReport[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);
  const [text, setText] = useState("");

  const searchBy = useDebounce(text, 500);

  const { sendRequest, loading } = useFetch(reportsViewService.getAll);

  const fetchReports = useCallback(async () => {
    const { data, success } = await sendRequest({
      PageNumber: page,
      PageSize: 25,
      SearchBy: searchBy,
      ReportTypeId: typeId,
    } as IReportsSearchParams);

    if (!success || !data) return;

    setHasNextPage(data.hasNextPage);
    setReports((prev) => (page === 1 ? data.items : [...prev, ...data.items]));
  }, [page, searchBy, sendRequest, typeId]);

  const searchHandler = useCallback((value: string) => {
    setReports([]);
    setHasNextPage(false);
    setPage(1);
    setText(value);
  }, []);

  const viewMoreHandler = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return (
    <Box
      sx={{
        pt: 2,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h5">{title}</Typography>
      </Box>
      <Box sx={{ pt: 2, width: "100%", maxWidth: 400 }}>
        <SearchInput
          onSearch={searchHandler}
          placeholder={
            t(REPORTS_TRANSLATION_PATH + "search") || "Search for a report"
          }
        />
      </Box>

      <Box
        sx={{
          mt: 4,
          pb: 2,
          height: "100%",
          overflow: "auto",
        }}
      >
        {!loading && reports.length === 0 && (
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <NoData
              message={
                t(REPORTS_TRANSLATION_PATH + "no_data") || "No reports found"
              }
            />
          </Box>
        )}

        {!loading && reports.length > 0 && (
          <Grid container spacing={2}>
            {reports.map((report) => (
              <Grid item xs={12} md={4} xl={3} key={report.id}>
                <ReportCard
                  report={report}
                  onClick={onReportClick}
                  onDelete={onReportDelete}
                  canEditDescription={canEditDescription}
                  onDescriptionSaved={onDescriptionSaved}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {(hasNextPage || loading) && (
          <ViewMore
            loading={loading}
            hasNextPage={hasNextPage}
            viewMoreHandler={viewMoreHandler}
          />
        )}
      </Box>
    </Box>
  );
};

export const ReportGridView = memo(ReportGridViewComponent);
